import { createAsyncThunk } from '@reduxjs/toolkit';
import ApiClient from 'src/helpers/apiClient';
import type { CandidateBoardingBody } from 'src/openapi';
import axios from 'axios';
import type { RootState } from 'src/redux/index';
import config from '../../../../config';

export const candidateOnboarding = createAsyncThunk(
  'candidate/onboarding',
  async (obj: CandidateBoardingBody, { rejectWithValue }) => {
    try {
      const response = await (
        await ApiClient.getJobApi()
      ).jobApiCandidateOnboarding({ candidateBoardingBody: obj });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

let getJobRoleListController: AbortController | null = null;
export const getJobRoleList = createAsyncThunk(
  'job-role/autocomplete',
  async (
    { query, orderBy }: { query: string; orderBy?: string },
    { rejectWithValue },
  ) => {
    try {
      if (getJobRoleListController) {
        getJobRoleListController.abort();
      }
      getJobRoleListController = new AbortController();

      const response = await (
        await ApiClient.getUserApi()
      ).userApiJobRoleAutoComplete(
        { orderBy, query },
        {
          signal: getJobRoleListController.signal,
        },
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

let getJobRoleSkillListController: AbortController | null = null;
export const getJobRoleSkillList = createAsyncThunk(
  'job-role/autocomplete',
  async (
    {
      query,
      searchType,
    }: { query: string; searchType?: 'skill' | 'job_role' | 'company_name' },
    { rejectWithValue },
  ) => {
    try {
      if (getJobRoleSkillListController) {
        getJobRoleSkillListController.abort();
      }
      getJobRoleSkillListController = new AbortController();

      const response = await (
        await ApiClient.getJobApi()
      ).jobApiGetJobSkillsAutocomplete(
        { searchQuery: query, searchType },
        {
          signal: getJobRoleSkillListController.signal,
        },
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const getSkillsList = createAsyncThunk(
  'job/user-skill',
  async ({ query }: { query: string }, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const response = await axios.get(`${config.API_URL}/job/user-skill`, {
        headers: {
          Authorization: `Bearer ${state.auth?.token as string}`,
        },
        params: {
          query,
        },
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const updatePreference = createAsyncThunk(
  'job/candidate-meta-details',
  async ({ obj }: { obj: any }, { rejectWithValue }) => {
    try {
      const response = await (
        await ApiClient.getJobApi()
      ).jobApiUpsertCandidateMetaDetails({ upsertCandidateDetailsBody: obj });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const sendCandidateOTP = createAsyncThunk(
  'sms/otp',
  async ({ obj }: { obj: {} }, { rejectWithValue }) => {
    try {
      const response = await (
        await ApiClient.getSmsApi()
      ).smsApiGenerateSms({ sendOtpBody: obj });
      return response.data;
    } catch (e: any) {
      return rejectWithValue(e.response.data);
    }
  },
);

export const verifyCandidateOTP = createAsyncThunk(
  'sms/otp',
  async ({ obj }: { obj: { otp: number } }) => {
    const response = await (
      await ApiClient.getSmsApi()
    ).smsApiValidateOtp({ validateOtpBody: obj });
    return response;
  },
);

export const onboardingDone = createAsyncThunk(
  'user/onboarding-done',
  async ({ name }: { name: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await ApiClient.getUserApi()
      ).userApiOnboardingDone({
        onboardingDoneBody: { name },
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const sendCollegeAutocompleteQuery = createAsyncThunk(
  'college/autocomplete',
  async (
    {
      query,
      page = 1,
      pageSize = 10,
    }: {
      page?: number;
      pageSize?: number;
      query?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await ApiClient.getUserApi()
      ).userApiCollegeAutocomplete({ page, pageSize, query });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const sendDegreeAutocompleteQuery = createAsyncThunk(
  'degree/autocomplete',
  async ({ query }: { query: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await ApiClient.getJobApi()
      ).jobApiDegreeAutoComplete({ query });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const sendSpecializationAutocompleteQuery = createAsyncThunk(
  'specialization/autocomplete',
  async (
    { query, degreeId }: { query: string; degreeId: any },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await ApiClient.getJobApi()
      ).jobApiSpecializationAutoComplete({ degreeId, query });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const checkUsernameAvailability = createAsyncThunk(
  'checkUsernameAvailability',
  async ({ username }: { username: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await ApiClient.getUserApi()
      ).userApiIsUsernameAvailable({ username });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);
